"use strict"

import React from "react"
import moment from "moment"
import { FormattedMessage } from "react-intl"
import { Button } from "react-bootstrap"

import { LanzaTable, UniversalFilter } from "../../shared_components/widgets/LanzaTable"
import { AppSettingConfigContext } from "../../config/AppSettingsConfigProvider"

import ModalDataExportLogs from "./modals/ModalDataExportLogs"
import ModalSendFile from "./modals/ModalSendFile"
import { logToServer } from "../../ErrorBoundary"
import { addNewObject, getObjectsList } from "../../Loaders"
import { LocaleFormattedDate, LocaleFormattedUser } from "../internationalization/LocaleFormatted"

/* React component to display table for data transfer request */
export default class DataExportTable extends React.Component {
  static contextType = AppSettingConfigContext

  constructor(props) {
    super(props)

    this.table = React.createRef()

    this.state = {
      isvisible_modal_logs: false,
      isvisible_modal_sendfile: false,
      curr_data_export_rq: null,
      manual_data_export_allowed: false,
    }

    // Bind Methods
    this.reloadTable = this.reloadTable.bind(this)
  }

  reloadTable = () => {
    if (this.table?.current) {
      this.table.current.refresh()
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.context.config && this.context.config.settings) {
      if (
        this.context.config.settings.MANUAL_DATAEXPORT_ALLOWED &&
        !this.context.config.settings.AUTO_DATAEXPORT_ALLOWED &&
        !this.state.manual_data_export_allowed
      ) {
        this.setState({
          manual_data_export_allowed: true,
        })
      } else if (
        !this.context.config.settings.MANUAL_DATAEXPORT_ALLOWED &&
        this.state.manual_data_export_allowed
      ) {
        this.setState({
          manual_data_export_allowed: false,
        })
      }
    }
  }

  getDataTransferRequests = async (params = {}) => {
    const urlParams = new URLSearchParams({
      format: "json",
      offset: params.skip || 0,
      limit: params.limit || 10,
    })
    if (params.sort) {
      urlParams.append("sort", params.sort)
      urlParams.append("order", params.order)
    }
    for (const [key, value] of Object.entries(params.filters)) {
      urlParams.append("filter_" + key, value)
    }

    return await getObjectsList("/lake/data-export-requests/?" + urlParams.toString()).then((data) => {
      return { data: data.rows, rowCount: data.total }
    })
  }

  onClick_regenerateFile = (data_export) => {
    if (
      !confirm(`Are you sure you want to regenerate file for data export request id '${data_export.id}'?`)
    ) {
      return false
    }

    addNewObject(`/lake/data-export-requests/${data_export.id}/regenerate_file/`, {})
      .then((_) => {
        alert("Success! Request queued for processing. Your file will be available soon.")
        this.reloadTable()
      })
      .catch((error) => {
        logToServer(error)
        alert(error.response.data)
      })
  }

  // ---------- Modals ----------
  // ----- Modal: Logs -----
  openModal_logs = (data_export_rq) => {
    this.setState({
      isvisible_modal_logs: true,
      curr_data_export_rq: data_export_rq,
    })
  }

  onHideModal_logs = () => {
    this.setState({
      isvisible_modal_logs: false,
      curr_data_export_rq: null,
    })
  }
  // ----- /Modal: Logs -----

  // ----- Modal: SendFile -----
  openModal_sendfile = (data_export_rq) => {
    this.setState({
      isvisible_modal_sendfile: true,
      curr_data_export_rq: data_export_rq,
    })
  }

  onHideModal_sendfile = () => {
    this.setState({
      isvisible_modal_sendfile: false,
      curr_data_export_rq: null,
    })
  }

  onModalSuccess_sendfile = (_) => {
    this.reloadTable()
  }
  // ----- /Modal: SendFile -----

  // ---------- /Modals ----------

  render() {
    const { manual_data_export_allowed } = this.state

    return (
      <>
        <LanzaTable
          ref={this.table} // Server Pagination Related
          getDataPage={this.getDataTransferRequests} // Server Pagination Related
          noDataMessageId={"datatransfer.no.data"} // Server Pagination Related
          className={"table table-bordered tbl_lanzatable table-hover"}
          columns={[
            {
              Header: <FormattedMessage id={"general.created.on"} />,
              accessor: "created_on",
              style: { width: "150px" },
              disableFilters: true,
              Cell: ({ value }) => {
                return <LocaleFormattedDate date={value} shouldParse={true} />
              },
            },
            {
              Header: <FormattedMessage id={"general.description"} />,
              accessor: "description",
              style: { wordBreak: "break-word", width: "200px" },
            },
            {
              Header: <FormattedMessage id={"datatransfer.column.time.period"} />,
              id: "time_period",
              style: { width: "200px" },
              disableFilters: true,
              accessor: (row) => {
                return (
                  <>
                    <div>
                      <LocaleFormattedDate date={row.start_datetime} shouldParse={true} /> -
                    </div>
                    <div>
                      <LocaleFormattedDate date={row.end_datetime} shouldParse={true} />
                    </div>
                  </>
                )
              },
            },
            {
              Header: <FormattedMessage id={"general.reactors"} />,
              accessor: "reactor_list",
              style: { maxWidth: "400px", minWidth: "200px" },
              disableSortBy: true,
              Cell: ({ value }) => {
                return value
                  .map((reactor) => {
                    return reactor.name
                  })
                  .join(", ")
              },
            },
            {
              Header: <FormattedMessage id={"datatransfer.column.requested.by"} />,
              accessor: "requested_by",
              Cell: ({ value }) => <LocaleFormattedUser user={value} />,
            },
            {
              Header: <FormattedMessage id={"general.file"} />,
              id: "file_download",
              className: "text-center",
              disableSortBy: true,
              disableFilters: true,
              accessor: (row) => {
                const expire_on = moment(row.expire_on)
                const now = moment()

                if (row.is_file_available) {
                  return (
                    <a
                      href={row.datadump_file}
                      target={"_blank"}
                      style={{
                        color: "var(--lanzalake-blue-primary)",
                        textDecoration: "underline",
                      }}
                    >
                      <span className={"fa fa-download"} />
                    </a>
                  )
                } else {
                  if (expire_on < now) {
                    return (
                      <Button
                        variant={"outline-primary"}
                        size={"sm"}
                        onClick={() => this.onClick_regenerateFile(row)}
                      >
                        Regenerate
                      </Button>
                    )
                  }
                }
              },
            },
            {
              Header: <FormattedMessage id={"general.status"} />,
              accessor: "status",
              style: { minWidth: "120px" },
              Filter: ({ column, preFilteredRows }) => (
                <UniversalFilter
                  column={column}
                  setFilter={column.setFilter}
                  preFilteredRows={preFilteredRows}
                  filterType="select"
                  customOptions={[
                    // lake.models.DataExportRequest
                    { value: "new", text: "New" },
                    { value: "queued_for_processing", text: "Queued for Processing" },
                    { value: "processing", text: "Processing" },
                    { value: "ready", text: "Ready" },
                    { value: "processing_failed", text: "Processing Failed" },
                    { value: "cancelled", text: "Cancelled" },
                    { value: "queued_for_sending", text: "Queued for Sending" },
                    { value: "sending", text: "Sending" },
                    { value: "send_success", text: "Send Success" },
                    { value: "send_failed", text: "Send Failed" },
                    { value: "terminated", text: "Terminated" },
                  ]}
                  isClearable={false}
                />
              ),
              Cell: ({ value }) => {
                return _render_status(value)
              },
              className: "text-center",
            },
            {
              Header: <FormattedMessage id={"datatransfer.column.message"} />,
              accessor: "message",
              Cell: ({ value }) => {
                return (
                  <div
                    style={{
                      maxHeight: "250px",
                      maxWidth: "250px",
                      overflowY: "auto",
                    }}
                  >
                    {value}
                  </div>
                )
              },
            },
            {
              Header: <FormattedMessage id={"datatransfer.column.expire.on"} />,
              accessor: "expire_on",
              style: { width: "150px" },
              disableFilters: true,
              Cell: ({ value }) => {
                return <LocaleFormattedDate date={value} shouldParse={true} />
              },
            },
            {
              Header: "",
              id: "logs",
              style: { width: "120px" },
              accessor: (row) => {
                return (
                  <>
                    <Button
                      variant={"outline-primary"}
                      size={"sm"}
                      onClick={() => this.openModal_logs(row)}
                      style={{ display: "flex", marginBottom: "5px" }}
                    >
                      <i className={"fa fa-book"} />
                      &nbsp;
                      <FormattedMessage id={"general.log"} />
                    </Button>
                    {manual_data_export_allowed &&
                      row.is_file_available &&
                      !["send_success", "sending"].includes(row.status) && (
                        <Button
                          variant={"outline-primary"}
                          size={"sm"}
                          onClick={() => this.openModal_sendfile(row)}
                          style={{ display: "flex" }}
                        >
                          <i className={"fa fa-paper-plane-o text-success"} />
                          &nbsp;
                          <FormattedMessage id={"datatransfer.sendfile"} />
                        </Button>
                      )}
                  </>
                )
              },
              className: "text-center",
              disableSortBy: true,
              disableFilters: true,
            },
          ]}
          allowSorting={true}
          allowFilters={true}
          initialTableOptions={{
            sortBy: [{ id: "created_on", desc: true }],
            pageSize: 10,
          }}
        />

        {/*----- Modals -----*/}
        {this.state.curr_data_export_rq && this.state.isvisible_modal_logs && (
          <ModalDataExportLogs
            show={this.state.isvisible_modal_logs}
            onHide={this.onHideModal_logs}
            dataExportRq={this.state.curr_data_export_rq}
          />
        )}

        {this.state.curr_data_export_rq && this.state.isvisible_modal_sendfile && (
          <ModalSendFile
            show={this.state.isvisible_modal_sendfile}
            onHide={this.onHideModal_sendfile}
            onSuccess={this.onModalSuccess_sendfile}
            dataExportRqID={this.state.curr_data_export_rq.id}
          />
        )}
        {/*----- /Modals -----*/}
      </>
    )
  }
}

export const _render_status = (status) => {
  switch (status) {
    case "new": {
      return (
        <span className={"text-info"}>
          <FormattedMessage id={"general.new"} description={"File Send Status"} />
        </span>
      )
    }
    case "queued_for_processing": {
      return (
        <span className={"text_black"}>
          <i className={"fa fa-clock-o"} style={{ marginRight: "2px" }} />
          <FormattedMessage
            id={"datatransfer.status.queued.for.processing"}
            description={"File Send Status"}
          />
        </span>
      )
    }
    case "processing": {
      return (
        <span>
          <i className={"fa fa-refresh"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.processing"} description={"File Send Status"} />
        </span>
      )
    }
    case "ready": {
      return (
        <span className={"text-success"}>
          <i className={"text-success fa fa-check"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.ready"} description={"File Send Status"} />
        </span>
      )
    }
    case "processing_failed": {
      return (
        <span className={"text-danger"}>
          <i className={"text-danger fa fa-times"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.processing.failed"} description={"File Send Status"} />
        </span>
      )
    }
    case "queued_for_sending": {
      return (
        <span className={"text_black"}>
          <i className={"fa fa-clock-o"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.queued.for.sending"} description={"File Send Status"} />
        </span>
      )
    }
    case "sending": {
      return (
        <span>
          <i className={"fa fa-refresh"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.sending"} description={"File Send Status"} />
        </span>
      )
    }
    case "send_success": {
      return (
        <span className={"text-success"}>
          <i className={"text-success fa fa-paper-plane-o"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.sent.successfully"} description={"File Send Status"} />
        </span>
      )
    }
    case "send_failed": {
      return (
        <span className={"text-danger"}>
          <i className={"text-danger fa fa-ban"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.send.failure"} description={"File Send Status"} />
        </span>
      )
    }
    case "terminated": {
      return (
        <span className={"text-success"}>
          <i className={"text-success fa fa-check"} style={{ marginRight: "2px" }} />
          <FormattedMessage id={"datatransfer.status.ready"} description={"File Send Status"} />
        </span>
      )
    }
    default:
      return status
  }
}
